import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Search, ChevronDown, ChevronUp, Trophy, Tag, Calendar, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { fmtDate } from '@/lib/utils';
import { EXPERIENCE_DATA, ADULT_RIG_DATA } from '@/components/booking/bookingDefaults';
import SessionResultEditor from '@/components/admin/SessionResultEditor';
import { useAuth } from '@/lib/AuthContext';

const expLabel = (k) => EXPERIENCE_DATA[k]?.name || k;
const rigLabel = (k) => (k === 'child_sim' ? 'Child Sim Rig' : ADULT_RIG_DATA[k]?.name) || k;

export default function AdminSessionResults() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const [search, setSearch] = useState('');
  const [resultsFilter, setResultsFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const queryClient = useQueryClient();

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['admin-bookings-for-results'],
    queryFn: () => base44.entities.Booking.list('-date', 1000),
    enabled: currentUser?.role === 'admin',
  });
  const { data: results = [], isLoading: resultsLoading } = useQuery({
    queryKey: ['admin-session-results'],
    queryFn: () => base44.entities.SessionResult.list('-session_date'),
    enabled: currentUser?.role === 'admin',
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-session-results'] });
    queryClient.invalidateQueries({ queryKey: ['session-results'] });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  const resultByBooking = {};
  results.forEach((r) => { if (r.booking_id) resultByBooking[r.booking_id] = r; });

  const q = search.toLowerCase();
  const filtered = bookings.filter((b) => {
    if (b.status === 'cancelled') return false;
    const hasResult = !!resultByBooking[b.id];
    if (resultsFilter === 'has' && !hasResult) return false;
    if (resultsFilter === 'missing' && hasResult) return false;
    if (!q) return true;
    return (b.customer_name || '').toLowerCase().includes(q)
      || (b.customer_email || '').toLowerCase().includes(q)
      || (b.date || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-10 md:py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">BOOKINGS & SIM</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Session Results</h1>
          <p className="text-white/30 text-sm mt-2 max-w-xl">
            Enter telemetry and results for each session. Drivers see their personal results on their account page.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-8 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or date..."
              className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60"
            />
          </div>
          <div className="flex gap-2">
            {[{ k: 'all', l: 'All' }, { k: 'missing', l: 'Missing Results' }, { k: 'has', l: 'Has Results' }].map((t) => (
              <button
                key={t.k}
                onClick={() => setResultsFilter(t.k)}
                className={`px-3 py-2 text-xs font-bold tracking-wider border transition-colors ${resultsFilter === t.k ? 'bg-primary border-primary text-white' : 'border-white/10 text-white/40 hover:border-primary/40'}`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>

        {bookingsLoading || resultsLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-white/5 bg-[#111115]">
            <Trophy className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No bookings found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((b) => {
              const existing = resultByBooking[b.id];
              const open = openId === b.id;
              return (
                <div key={b.id} className="bg-[#111115] border border-white/5">
                  <button
                    onClick={() => setOpenId(open ? null : b.id)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        {existing && (
                          <span className="inline-flex items-center gap-1 bg-primary/10 text-primary text-[10px] font-bold tracking-widest px-2 py-1">
                            <Trophy className="w-3 h-3" /> RESULTS
                          </span>
                        )}
                        <p className="text-white font-bold text-sm">{b.customer_name || 'Unknown driver'}</p>
                      </div>
                      <p className="text-white/30 text-xs">{b.customer_email}</p>
                      <p className="text-white/40 text-xs mt-1 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {fmtDate(b.date) || '—'} · {b.time_slot}
                        </span>
                        <span>{expLabel(b.experience_type)}</span>
                        {b.rig_type && (
                          <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{rigLabel(b.rig_type)}</span>
                        )}
                      </p>
                    </div>
                    {open ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                  </button>
                  {open && (
                    <SessionResultEditor
                      booking={b}
                      existing={existing}
                      onSaved={() => { invalidate(); setOpenId(null); }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}